import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, X, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import ContractorOverviewTab from '@/components/ContractorOverviewTab';

const STATUS_PILL = {
  'Quoted':            'bg-blue-100 text-blue-700',
  'Quote Sent':        'bg-indigo-100 text-indigo-700',
  'Accepted':          'bg-emerald-100 text-emerald-700',
  'Work Booked':       'bg-amber-100 text-amber-700',
  'Work In Progress':  'bg-orange-100 text-orange-700',
  'Completed':         'bg-emerald-100 text-emerald-700',
  'Invoiced':          'bg-violet-100 text-violet-700',
  'Paid':              'bg-emerald-100 text-emerald-700',
};

function fmtMoney(v) {
  const n = Number(v || 0);
  if (!n) return '—';
  return `$${n.toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;
}

function labelFor(a) {
  const ref = a.appraisal_no ? `A${a.appraisal_no}` : `#${(a._id || '').slice(-6)}`;
  const who = a.client_name || 'Unknown client';
  const where = a.property_address || '';
  return { ref, who, where };
}

export default function ContractorReportsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // full appraisal object
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced server-side search (handles 4,700+ appraisals fast).
  useEffect(() => {
    if (selected) return; // when something is locked in, don't search
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/appraisals?page=1&limit=30&search=${encodeURIComponent(query)}&status=&client_id=&sort_by=updated_at&sort_dir=desc`
        );
        setResults(data.items || []);
        setHighlight(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, query ? 220 : 0);
    return () => clearTimeout(t);
  }, [api, query, selected]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = (a) => {
    setSelected(a);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    setSelected(null);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlight]) pick(results[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-[1600px] mx-auto space-y-5" data-testid="contractor-reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users size={28} className="text-orange-600" />
            Contractor Reports
          </h1>
          <p className="text-sm text-slate-500 mt-1.5">
            Search and pick an appraisal to assign trades, set contractor rates, and generate the printable Contractor Sheet (Iron / Concrete / Painter / Gutter / Repair / All).
          </p>
        </div>
      </div>

      {/* Combobox picker */}
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Appraisal</label>
        <div ref={wrapRef} className="relative" data-testid="appraisal-combobox">
          {selected ? (
            <SelectedChip
              appraisal={selected}
              onChange={clear}
              onOpenAppraisal={() => navigate(`/appraisals/${selected._id}?tab=Contractors`)}
            />
          ) : (
            <div className="flex items-center border border-slate-300 rounded-md bg-white focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500">
              <Search size={16} className="ml-3 text-slate-400 flex-shrink-0" />
              <input
                ref={inputRef}
                data-testid="appraisal-search-input"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder="Type to search by appraisal #, client name, or property address…"
                className="flex-1 px-2 py-2.5 text-sm bg-transparent outline-none"
                autoComplete="off"
              />
              <ChevronDown
                size={16}
                className={`mr-3 text-slate-400 cursor-pointer transition-transform ${open ? 'rotate-180' : ''}`}
                onClick={() => setOpen(v => !v)}
              />
            </div>
          )}

          {!selected && open && (
            <div
              data-testid="appraisal-combobox-list"
              className="absolute left-0 right-0 top-full mt-1 z-30 max-h-[480px] overflow-y-auto border border-slate-200 bg-white rounded-md shadow-lg"
            >
              {loading && (
                <div className="px-3 py-3 text-xs text-slate-400">Searching…</div>
              )}
              {!loading && results.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-slate-400">
                  {query ? `No appraisals match "${query}"` : 'Start typing to search'}
                </div>
              )}
              {!loading && results.map((a, idx) => {
                const { ref, who, where } = labelFor(a);
                const pill = STATUS_PILL[a.appraisal_status] || 'bg-slate-100 text-slate-600';
                const active = idx === highlight;
                return (
                  <button
                    key={a._id}
                    data-testid={`appraisal-option-${a._id}`}
                    type="button"
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => pick(a)}
                    className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 transition-colors ${active ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-orange-700">{ref}</span>
                          <span className="text-sm font-semibold text-slate-900 truncate">{who}</span>
                          {a.service_option && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{a.service_option}</span>
                          )}
                        </div>
                        {where && (
                          <div className="text-xs text-slate-500 truncate mt-0.5">{where}</div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${pill}`}>
                          {a.appraisal_status || '—'}
                        </span>
                        <span className="text-xs tabular-nums text-slate-500">{fmtMoney(a.calc_quote_value || a.contract_amount)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="text-[11px] text-slate-400 mt-2">
          {selected
            ? <>Selected appraisal locked in. Click <b>Change</b> to pick another.</>
            : <>Type at least one character — results update as you type. Use ↑/↓ + Enter to pick.</>}
        </div>
      </div>

      {selected ? (
        <ContractorOverviewTab api={api} appraisalId={selected._id} />
      ) : (
        <div className="rounded-md border border-dashed border-slate-300 p-12 text-center text-slate-400">
          Search and pick an appraisal above to view its contractor overview.
        </div>
      )}
    </div>
  );
}

function SelectedChip({ appraisal, onChange, onOpenAppraisal }) {
  const { ref, who, where } = labelFor(appraisal);
  const pill = STATUS_PILL[appraisal.appraisal_status] || 'bg-slate-100 text-slate-600';
  return (
    <div className="flex items-center justify-between gap-3 border-2 border-orange-400 bg-orange-50/40 rounded-md px-3 py-2.5" data-testid="selected-appraisal-chip">
      <div className="flex items-center gap-3 min-w-0">
        <Check size={16} className="text-orange-600 flex-shrink-0" />
        <span className="font-mono text-xs font-bold text-orange-700 flex-shrink-0">{ref}</span>
        <span className="text-sm font-semibold text-slate-900 truncate">{who}</span>
        {where && <span className="hidden md:inline text-xs text-slate-500 truncate">· {where}</span>}
        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${pill}`}>
          {appraisal.appraisal_status || '—'}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          data-testid="open-in-appraisal-btn"
          onClick={onOpenAppraisal}
          className="text-xs px-2.5 py-1 rounded border border-slate-300 text-slate-700 hover:bg-white"
        >
          Open inside appraisal
        </button>
        <button
          data-testid="change-appraisal-btn"
          onClick={onChange}
          className="text-xs px-2.5 py-1 rounded border border-slate-300 text-slate-700 hover:bg-white flex items-center gap-1"
        >
          <X size={12} /> Change
        </button>
      </div>
    </div>
  );
}
